import { createContext, ReactNode, RefObject, useContext } from 'react';

/**
 * Portal Root Context
 *
 * Provides a mechanism to render FloatingPortal elements into a specific container
 * instead of document.body. This is essential for proper z-index handling when
 * portals are opened inside modals - rendering into the modal's DOM keeps them
 * in the same stacking context.
 *
 * Usage:
 * 1. Modal provides PortalRootProvider with its container ref
 * 2. SmartFloatingPortal uses usePortalRoot() to get the container
 * 3. When null (no modal), portals render to document.body as usual
 */

const PortalRootContext = createContext<HTMLElement | null>(null);

interface PortalRootProviderProps {
  children: ReactNode;
  containerRef: RefObject<HTMLElement | null>;
}

/**
 * Provider that sets the portal root for descendant FloatingPortal components.
 * Should wrap modal children to ensure dropdowns render into the modal's DOM.
 */
export function PortalRootProvider({ children, containerRef }: PortalRootProviderProps) {
  return (
    <PortalRootContext.Provider value={containerRef.current}>{children}</PortalRootContext.Provider>
  );
}

/**
 * Hook to get the current portal root from context.
 * Returns null when not inside a PortalRootProvider (render to body).
 */
export function usePortalRoot(): HTMLElement | null {
  return useContext(PortalRootContext);
}

export { PortalRootContext };
